import React, { useState } from "react";
import { usePodeEditar } from "@hooks/usePodeEditar";
import { clienteService } from "@services/clienteService";
import { Cliente, CreateClienteDTO } from "@models/index";
import {
  Button,
  Input,
  Modal,
  ServerDataTable,
  Column,
  DataTableAction,
} from "@components/common";
import { useServerPagination } from "@hooks/useServerPagination";
import { useFeedback } from "@contexts/FeedbackContext";
import {
  HiPlus,
  HiTrash,
  HiRefresh,
  HiPencil,
  HiX,
  HiSave,
} from "react-icons/hi";
import "./pages-common.css";

/** Padrão do sistema quando o cliente não tem percentual próprio. */
const HONORARIOS_PADRAO = 8;

const honorariosDoCliente = (cliente: Cliente) =>
  cliente.percentHonorarios ?? HONORARIOS_PADRAO;

/** Texto do campo → número, ou null quando vazio ou fora de 0–100. */
const lerPercentual = (texto: string): number | null => {
  const n = Number(texto.replace(",", "."));
  return texto.trim() !== "" && Number.isFinite(n) && n >= 0 && n <= 100
    ? n
    : null;
};

interface EdicaoCliente {
  cliente: Cliente;
  nome: string;
  email: string;
  telefone: string;
  cnpj: string;
  honorarios: string;
}

export const Clientes: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [formData, setFormData] = useState<CreateClienteDTO>({
    nome: "",
    email: "",
    telefone: "",
    cnpj: "",
    endereco: "",
  });
  const [honorariosNovo, setHonorariosNovo] = useState(
    String(HONORARIOS_PADRAO),
  );
  const [edicao, setEdicao] = useState<EdicaoCliente | null>(null);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const { confirm, showError, showSuccess } = useFeedback();
  const podeEditar = usePodeEditar();

  // Hook de paginação server-side
  const {
    data: clientes,
    loading,
    pagination,
    setPage,
    setPageSize,
    setSearch,
    setSortBy,
    setSortOrder,
    refresh,
  } = useServerPagination({
    fetchFunction: clienteService.getAll,
    initialPageSize: 10,
    initialSortBy: "nome",
    initialSortOrder: "asc",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const percentHonorarios = lerPercentual(honorariosNovo);
    if (percentHonorarios === null) {
      showError("Informe o honorário entre 0% e 100%.");
      return;
    }
    try {
      await clienteService.create({ ...formData, percentHonorarios });
      setHonorariosNovo(String(HONORARIOS_PADRAO));
      setShowForm(false);
      setFormData({
        nome: "",
        email: "",
        telefone: "",
        cnpj: "",
        endereco: "",
      });
      refresh();
      showSuccess("Cliente cadastrado com sucesso!");
    } catch (err) {
      console.error(err);
      showError(
        "Não foi possível cadastrar o cliente. Verifique os dados e tente novamente.",
      );
    }
  };

  const abrirEdicao = (cliente: Cliente) =>
    setEdicao({
      cliente,
      nome: cliente.nome ?? "",
      email: cliente.email ?? "",
      telefone: cliente.telefone ?? "",
      cnpj: cliente.cnpj ?? "",
      honorarios: String(honorariosDoCliente(cliente)),
    });

  const salvarEdicao = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!edicao) return;
    if (!edicao.nome.trim()) {
      showError("O nome é obrigatório.");
      return;
    }
    const percentHonorarios = lerPercentual(edicao.honorarios);
    if (percentHonorarios === null) {
      showError("Informe o honorário entre 0% e 100%.");
      return;
    }

    // Mudar o honorário muda os totais de todas as planilhas do cliente,
    // inclusive as já fechadas, porque o percentual é lido do cliente a cada
    // abertura. Vale a pena o usuário saber antes de salvar.
    if (percentHonorarios !== honorariosDoCliente(edicao.cliente)) {
      const ok = await confirm({
        title: "Alterar honorário",
        message: `Os honorários de todas as planilhas de "${edicao.cliente.nome}" passam de ${honorariosDoCliente(edicao.cliente)}% para ${percentHonorarios}%, inclusive as já existentes, a partir da próxima vez que forem abertas ou exportadas.`,
        confirmText: "Alterar",
        cancelText: "Cancelar",
      });
      if (!ok) return;
    }

    try {
      setSalvandoEdicao(true);
      await clienteService.update(edicao.cliente.id, {
        nome: edicao.nome.trim(),
        email: edicao.email.trim(),
        telefone: edicao.telefone.trim(),
        cnpj: edicao.cnpj.trim(),
        percentHonorarios,
      });
      setEdicao(null);
      refresh();
      showSuccess("Cliente atualizado.");
    } catch (err) {
      console.error(err);
      showError("Não foi possível salvar o cliente.");
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const handleDelete = async (cliente: Cliente) => {
    const confirmed = await confirm({
      title: "Excluir cliente",
      message: `Tem certeza que deseja excluir "${cliente.nome}"? Esta ação não poderá ser desfeita.`,
      confirmText: "Excluir",
      cancelText: "Cancelar",
      tone: "danger",
    });

    if (!confirmed) return;

    try {
      await clienteService.delete(cliente.id);
      refresh();
      showSuccess("Cliente excluído com sucesso.");
    } catch (err) {
      console.error(err);
      showError("Não foi possível excluir este cliente.");
    }
  };

  const handleSyncWrike = async () => {
    const confirmed = await confirm({
      title: "Sincronizar com Wrike",
      message:
        "Deseja importar todos os clientes do Wrike? Esta operação usa sincronização em massa (bulk) e é muito rápida.",
      confirmText: "Sincronizar",
      cancelText: "Cancelar",
      tone: "default",
    });

    if (!confirmed) return;

    try {
      setSyncing(true);
      const result = await clienteService.syncFromWrike();

      refresh();
      showSuccess(
        `Sincronização concluída! ${result.totalProcessed} clientes processados em ${result.tempoDecorrido} (${result.velocidade})`,
      );
    } catch (err) {
      console.error(err);
      showError("Erro ao sincronizar com Wrike.");
    } finally {
      setSyncing(false);
    }
  };

  const handleSortChange = (sortByKey: string, order: "asc" | "desc") => {
    setSortBy(sortByKey);
    setSortOrder(order);
  };

  // Definir colunas da tabela
  const columns: Column<Cliente>[] = [
    {
      key: "nome",
      label: "Nome",
      sortable: true,
      width: "50%",
    },
    {
      key: "cnpj",
      label: "CNPJ",
      sortable: true,
      width: "30%",
      render: (cliente) => cliente.cnpj || "-",
    },
    {
      key: "percentHonorarios",
      label: "Honorários",
      width: "10%",
      render: (cliente) =>
        `${honorariosDoCliente(cliente).toLocaleString("pt-BR")}%`,
    },
  ];

  // Definir ações da tabela
  // Visualizador consulta, sem editar nem excluir.
  const actions: DataTableAction<Cliente>[] = !podeEditar ? [] : [
    {
      icon: <HiPencil />,
      onClick: abrirEdicao,
      variant: "success",
      tooltip: "Editar cliente",
    },
    {
      icon: <HiTrash />,
      onClick: handleDelete,
      variant: "danger",
      tooltip: "Excluir cliente",
    },
  ];

  return (
    <div className="crud-page">
      <div className="page-header">
        <h1>Clientes</h1>
        <p>Gerencie os clientes cadastrados no sistema</p>
      </div>

      {/* Formulário de cadastro */}
      {showForm && (
        <div className="form-card">
          <div className="form-header">
            <h2>Novo Cliente</h2>
            <Button
              variant="secondary"
              size="small"
              onClick={() => {
                setShowForm(false);
                setFormData({
                  nome: "",
                  email: "",
                  telefone: "",
                  cnpj: "",
                  endereco: "",
                });
              }}
            >
              <HiX />
            </Button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <Input
                label="Nome *"
                value={formData.nome}
                onChange={(e) =>
                  setFormData({ ...formData, nome: e.target.value })
                }
                required
              />
              <Input
                label="CNPJ"
                value={formData.cnpj || ""}
                onChange={(e) =>
                  setFormData({ ...formData, cnpj: e.target.value })
                }
              />
              <Input
                label="E-mail *"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
              />
              <Input
                label="Telefone"
                value={formData.telefone || ""}
                onChange={(e) =>
                  setFormData({ ...formData, telefone: e.target.value })
                }
              />
              <Input
                label="Honorários (%) *"
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={honorariosNovo}
                onChange={(e) => setHonorariosNovo(e.target.value)}
                helperText="Sobre os serviços complementares. Taxas e seguros seguem em 6%."
                required
              />
              <Input
                label="Endereço"
                value={formData.endereco || ""}
                onChange={(e) =>
                  setFormData({ ...formData, endereco: e.target.value })
                }
                fullWidth
              />
            </div>
            <div className="form-actions">
              <Button type="submit" variant="primary">
                Salvar Cliente
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowForm(false);
                  setFormData({
                    nome: "",
                    email: "",
                    telefone: "",
                    cnpj: "",
                    endereco: "",
                  });
                }}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      )}

      <Modal
        isOpen={!!edicao}
        onClose={() => setEdicao(null)}
        title="Editar Cliente"
        size="medium"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setEdicao(null)}
              disabled={salvandoEdicao}
            >
              <HiX className="h-4 w-4" />
              Cancelar
            </Button>
            <Button
              type="submit"
              form="editar-cliente-form"
              variant="success"
              loading={salvandoEdicao}
              disabled={salvandoEdicao}
            >
              <HiSave className="h-4 w-4" />
              Salvar
            </Button>
          </>
        }
      >
        {edicao && (
          <form
            id="editar-cliente-form"
            onSubmit={salvarEdicao}
            className="space-y-4"
          >
            <Input
              label="Nome *"
              value={edicao.nome}
              onChange={(e) => setEdicao({ ...edicao, nome: e.target.value })}
              required
              fullWidth
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="CNPJ"
                value={edicao.cnpj}
                onChange={(e) =>
                  setEdicao({ ...edicao, cnpj: e.target.value })
                }
                fullWidth
              />
              <Input
                label="Honorários (%) *"
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={edicao.honorarios}
                onChange={(e) =>
                  setEdicao({ ...edicao, honorarios: e.target.value })
                }
                helperText="Sobre os serviços complementares. Taxas e seguros seguem em 6%."
                required
                fullWidth
              />
              <Input
                label="E-mail"
                type="email"
                value={edicao.email}
                onChange={(e) =>
                  setEdicao({ ...edicao, email: e.target.value })
                }
                fullWidth
              />
              <Input
                label="Telefone"
                value={edicao.telefone}
                onChange={(e) =>
                  setEdicao({ ...edicao, telefone: e.target.value })
                }
                fullWidth
              />
            </div>
          </form>
        )}
      </Modal>

      {/* Tabela com paginação server-side */}
      <ServerDataTable
        data={clientes}
        columns={columns}
        actions={actions}
        loading={loading}
        pagination={pagination}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nome, email, CNPJ, telefone..."
        onSortChange={handleSortChange}
        keyExtractor={(cliente) => cliente.id}
        emptyMessage="Nenhum cliente cadastrado ainda"
        headerActions={
          podeEditar && (
          <>
            <Button
              variant="success"
              onClick={handleSyncWrike}
              disabled={syncing}
            >
              <HiRefresh className={syncing ? "spinning" : ""} />
              {syncing ? "Sincronizando..." : "Sincronizar Wrike"}
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowForm(true)}
              disabled={showForm}
            >
              <HiPlus />
              Novo Cliente
            </Button>
          </>
          )
        }
      />
    </div>
  );
};
